import React, { useEffect, useState } from 'react';
import {
    AlertCircle,
    AlertTriangle,
    CheckCircle,
    Info,
    Download,
    Bug,
    StopCircle,
    TerminalSquare,
    Copy,
} from 'lucide-react';
import { AgentProps } from '../Interfaces/AgentProps';

type LogType = {
    level: 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR';
    timestamp: string;
    source: string;
    message: string;
};

const sampleLogs: LogType[] =
    [
        { "level": "INFO", "timestamp": "", "source": "System", "message": "Initializing agent session..." },
        { "level": "SUCCESS", "timestamp": "", "source": "Builder", "message": "Agent compiled successfully." },
        { "level": "WARNING", "timestamp": "", "source": "Vector Engine", "message": "Low semantic match confidence." },
        { "level": "ERROR", "timestamp": "", "source": "DataService", "message": "Timeout fetching customer data." },
        { "level": "INFO", "timestamp": "", "source": "Agent", "message": "Retrying with safe fallback config." },
        { "level": "INFO", "timestamp": "", "source": "System", "message": "Heartbeat check passed." },
        { "level": "SUCCESS", "timestamp": "", "source": "Deployment", "message": "Version 3.2.1 deployed successfully." },
        { "level": "WARNING", "timestamp": "", "source": "Parser", "message": "Deprecated field detected in input payload." },
        { "level": "ERROR", "timestamp": "", "source": "Storage", "message": "Disk quota exceeded." },
        { "level": "INFO", "timestamp": "", "source": "Auth", "message": "User session verified." },
        { "level": "ERROR", "timestamp": "", "source": "Database", "message": "Connection pool exhausted." },
        { "level": "INFO", "timestamp": "", "source": "Scheduler", "message": "Next task scheduled in 15 minutes." },
        { "level": "SUCCESS", "timestamp": "", "source": "Training", "message": "Model training completed with 98% accuracy." },
        { "level": "WARNING", "timestamp": "", "source": "Monitor", "message": "Memory usage at 85% threshold." },
        { "level": "INFO", "timestamp": "", "source": "Agent", "message": "Reinitializing configuration..." },
        { "level": "SUCCESS", "timestamp": "", "source": "Diagnostics", "message": "Health check passed on all nodes." },
        { "level": "ERROR", "timestamp": "", "source": "Backup", "message": "Incremental backup failed." },
        { "level": "INFO", "timestamp": "", "source": "System", "message": "Graceful shutdown in progress..." },
        { "level": "WARNING", "timestamp": "", "source": "LoadBalancer", "message": "Response latency above normal levels." },
        { "level": "INFO", "timestamp": "", "source": "Builder", "message": "Loading configuration profiles." },
        { "level": "SUCCESS", "timestamp": "", "source": "Controller", "message": "Failover node activated." },
        { "level": "ERROR", "timestamp": "", "source": "Auth", "message": "Invalid token signature detected." },
        { "level": "INFO", "timestamp": "", "source": "Logger", "message": "Log rotation completed." },
        { "level": "WARNING", "timestamp": "", "source": "Cache", "message": "Eviction threshold approaching limit." },
        { "level": "ERROR", "timestamp": "", "source": "API Gateway", "message": "Rate limit exceeded for IP 10.0.0.8." },
        { "level": "INFO", "timestamp": "", "source": "Updater", "message": "Patch applied to core module." },
        { "level": "SUCCESS", "timestamp": "", "source": "Migration", "message": "Database migration executed successfully." },
        { "level": "WARNING", "timestamp": "", "source": "Security", "message": "Unusual login attempt detected." },
        { "level": "INFO", "timestamp": "", "source": "Validator", "message": "Schema check passed for new payload." },
        { "level": "ERROR", "timestamp": "", "source": "QueueService", "message": "Message delivery failed after 3 retries." },
        { "level": "INFO", "timestamp": "", "source": "System", "message": "Auto-scaling policy triggered." },
        { "level": "WARNING", "timestamp": "", "source": "Indexing", "message": "Index rebuild taking longer than expected." },
        { "level": "ERROR", "timestamp": "", "source": "NodeManager", "message": "Node 5 unreachable." },
        { "level": "SUCCESS", "timestamp": "", "source": "Startup", "message": "System booted in 42 seconds." },
        { "level": "INFO", "timestamp": "", "source": "Monitor", "message": "CPU load normalized." },
        { "level": "WARNING", "timestamp": "", "source": "API Gateway", "message": "Deprecated endpoint usage increasing." },
        { "level": "ERROR", "timestamp": "", "source": "Service Mesh", "message": "TLS handshake failed." },
        { "level": "SUCCESS", "timestamp": "", "source": "Cleanup", "message": "Temporary files removed." },
        { "level": "INFO", "timestamp": "", "source": "Agent", "message": "Listening on port 443." }

    ];

const getIcon = (level: string) => {
    switch (level) {
        case 'ERROR':
            return <AlertCircle className="w-4 h-4 text-red-500" />;
        case 'WARNING':
            return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
        case 'SUCCESS':
            return <CheckCircle className="w-4 h-4 text-green-400" />;
        default:
            return <Info className="w-4 h-4 text-blue-400" />;
    }
};
type Props = {
  agent?: AgentProps;
};

const StreamingLogs: React.FC<Props> = ({ agent }) => {
    const [logs, setLogs] = useState<LogType[]>([]);
    const [debugMode, setDebugMode] = useState(false);
    const [logIndex, setLogIndex] = useState(0);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (debugMode && logIndex < sampleLogs.length) {
            interval = setTimeout(() => {
                const logToAdd = {
                    ...sampleLogs[logIndex],
                    timestamp: new Date().toLocaleTimeString(),
                };
                setLogs(prev => [logToAdd, ...prev]); // Newest log at the top
                setLogIndex(prev => prev + 1);
            }, 1500);
        }
        return () => clearTimeout(interval);
    }, [debugMode, logIndex]);

    const handleDownload = () => {
        const text = logs.map(log =>
            `[${log.timestamp}] [${log.level}] [${log.source}] - ${log.message}`
        ).join('\n');
        const blob = new Blob([text], { type: 'text/plain' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'agent_logs.txt';
        link.click();
    };

    const handleCopy = (log: LogType) => {
        const line = `[${log.timestamp}] [${log.level}] [${log.source}] - ${log.message}`;
        navigator.clipboard.writeText(line);
    };

    return (
        <div className="bg-neutral-950 border border-neutral-800 rounded-md text-sm max-h-[70vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center border-b border-neutral-800 px-4 py-2 bg-neutral-900">
                <div className="flex items-center gap-2 text-white text-sm font-medium">
                    <TerminalSquare className="w-4 h-4 text-indigo-500" />
                    Live Agent Logs
                </div>

                <div className="flex items-center gap-3 text-gray-400">
                    <button title="Download Logs" onClick={handleDownload}>
                        <Download className="w-4 h-4 hover:text-white" />
                    </button>
                    <button
                        title="Start Debug Mode"
                        onClick={() => {
                            setDebugMode(true);
                            setLogIndex(0);
                            setLogs([]);
                        }}
                    >
                        <Bug className={`w-4 h-4 ${debugMode ? 'text-yellow-400' : 'hover:text-white'}`} />
                    </button>
                    <button title="Stop Agent">
                        <StopCircle className="w-4 h-4 hover:text-red-500" />
                    </button>
                </div>
            </div>

            {/* Log Stream */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-black text-gray-200 text-xs font-mono">
                {logs.map((log, i) => (
                    <div key={i} className="flex items-start gap-2 hover:bg-neutral-900 rounded-sm px-1 py-1 group">
                        <span className="mt-0.5">{getIcon(log.level)}</span>
                        <div className="flex-1">
                            <div className="text-gray-400">
                                [{log.timestamp}] <span className="text-gray-500">[{log.source}]</span>
                            </div>
                            <div
                                className={`ml-1 ${log.level === 'ERROR'
                                    ? 'text-red-400'
                                    : log.level === 'WARNING'
                                        ? 'text-yellow-300'
                                        : 'text-gray-300'
                                    }`}
                            >
                                {log.message}
                            </div>
                        </div>
                        <button
                            title="Copy log"
                            className="text-gray-400 opacity-0 group-hover:opacity-100 hover:text-white"
                            onClick={() => handleCopy(log)}
                        >
                            <Copy className="w-4 h-4" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default StreamingLogs;
